import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import inventoryService from '../services/inventoryService';
import LoadingSpinner from './LoadingSpinner';
import ConfirmModal from './ConfirmModal';
import { ArrowDownLeft, ArrowUpRight, RefreshCw, Package, TrendingUp, TrendingDown } from 'lucide-react';

const WarehousesManagement = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [search, setSearch] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [movementTypeFilter, setMovementTypeFilter] = useState('');
  const [movementsPage, setMovementsPage] = useState(1);

  const [formData, setFormData] = useState({
    nome: '',
    codigo: '',
    ativo: true,
  });

  useEffect(() => {
    loadWarehouses();
  }, []);

  const loadWarehouses = async () => {
    try {
      setLoading(true);
      const response = await inventoryService.getWarehouses();
      setWarehouses(response.data || []);
    } catch (err) {
      console.error(err);
      setError('Erro ao carregar armazéns.');
    } finally {
      setLoading(false);
    }
  };

  const { data: movementsData, isLoading: loadingMovements, refetch: refetchMovements } = useQuery({
    queryKey: ['stock-movements', selectedWarehouseId, movementTypeFilter, movementsPage],
    queryFn: () => inventoryService.getStockMovements({
      armazem_id: selectedWarehouseId || undefined,
      tipo: movementTypeFilter || undefined,
      page: movementsPage,
      limit: 20
    }),
    enabled: true
  });

  const { data: stockData, isLoading: loadingStock } = useQuery({
    queryKey: ['warehouse-stock', selectedWarehouseId],
    queryFn: () => inventoryService.getStock({
      armazem_id: selectedWarehouseId || undefined,
      limit: 1000
    }),
    enabled: !!selectedWarehouseId
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingWarehouse) {
        await inventoryService.updateWarehouse(editingWarehouse.id, formData);
      } else {
        await inventoryService.createWarehouse(formData);
      }

      setShowForm(false);
      setEditingWarehouse(null);
      resetForm();
      loadWarehouses();
    } catch (err) {
      console.error(err);
      setError('Erro ao salvar armazém.');
    }
  };

  const handleEdit = (warehouse) => {
    setEditingWarehouse(warehouse);
    setFormData({
      nome: warehouse.nome,
      codigo: warehouse.codigo || '',
      ativo: warehouse.ativo,
    });
    setShowForm(true);
  };

  const handleDelete = (id) => {
    setPendingDeleteId(id);
    setIsConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await inventoryService.deleteWarehouse(pendingDeleteId);
      loadWarehouses();
    } catch (err) {
      console.error(err);
      setError('Erro ao desativar armazém.');
    } finally {
      setIsConfirmOpen(false);
      setPendingDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setIsConfirmOpen(false);
    setPendingDeleteId(null);
  };

  const handleNew = () => {
    setEditingWarehouse(null);
    resetForm();
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      codigo: '',
      ativo: true,
    });
  };

  const filteredWarehouses = warehouses.filter((w) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      w.nome.toLowerCase().includes(term) ||
      (w.codigo || '').toLowerCase().includes(term)
    );
  });

  const movements = movementsData?.data || [];
  const movementsPagination = movementsData?.pagination || {};

  const stockItems = stockData?.data || [];
  const stockStats = {
    totalQuantity: stockItems.reduce((sum, item) => sum + (item.quantidade || 0), 0),
    totalAvailable: stockItems.reduce((sum, item) => sum + (item.disponivel || 0), 0),
    totalReserved: stockItems.reduce((sum, item) => sum + (item.reservado || 0), 0),
    lowStockItems: stockItems.filter(item => (item.disponivel || 0) <= 5).length
  };

  const getMovementTypeLabel = (tipo) => {
    const labels = {
      'IN': 'Entrada',
      'OUT': 'Saída',
      'TRANSFER': 'Transferência',
      'AJUSTE': 'Ajuste'
    };
    return labels[tipo] || tipo;
  };

  const getMovementIcon = (tipo) => {
    switch (tipo) {
      case 'IN':
        return <ArrowDownLeft className="h-4 w-4 text-green-600" />;
      case 'OUT':
        return <ArrowUpRight className="h-4 w-4 text-red-600" />;
      case 'TRANSFER':
        return <RefreshCw className="h-4 w-4 text-blue-600" />;
      case 'AJUSTE':
        return <RefreshCw className="h-4 w-4 text-yellow-600" />;
      default:
        return <Package className="h-4 w-4 text-gray-400" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            📦 Gestão de Armazéns
          </h1>
          <p className="text-gray-600">
            Cadastre e gerencie os locais físicos onde o estoque é armazenado.
          </p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Filtros e ações */}
        <div className="bg-white p-4 rounded-lg shadow mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nome ou código do armazém..."
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <button
              onClick={handleNew}
              className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              ➕ Novo Armazém
            </button>
          </div>
        </div>

        {/* Seletor de armazém para ver movimentos */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Selecione um armazém para ver movimentos de estoque
              </label>
              <select
                value={selectedWarehouseId}
                onChange={(e) => {
                  setSelectedWarehouseId(e.target.value);
                  setMovementsPage(1);
                }}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Selecione um armazém...</option>
                {warehouses.filter(w => w.ativo).map(warehouse => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.nome} {warehouse.codigo ? `(${warehouse.codigo})` : ''}
                  </option>
                ))}
              </select>
            </div>
            {selectedWarehouseId && (
              <div className="flex gap-2">
                <button
                  onClick={() => refetchMovements()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Atualizar
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Formulário */}
        {showForm && (
          <div className="bg-white p-6 rounded-lg shadow mb-6">
            <h2 className="text-xl font-bold mb-4">
              {editingWarehouse ? '✏️ Editar Armazém' : '➕ Novo Armazém'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Armazém *
                  </label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: Armazém Principal"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código
                  </label>
                  <input
                    type="text"
                    name="codigo"
                    value={formData.codigo}
                    onChange={handleInputChange}
                    placeholder="Ex: ARM001"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Código curto para identificar o armazém em relatórios.
                  </p>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    id="ativo"
                    name="ativo"
                    checked={formData.ativo}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label htmlFor="ativo" className="text-sm text-gray-700">
                    Ativo
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingWarehouse(null);
                    resetForm();
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingWarehouse ? 'Atualizar' : 'Criar'} Armazém
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Lista de Armazéns */}
        <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">
              📋 Lista de Armazéns ({filteredWarehouses.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredWarehouses.map((w) => (
                  <tr key={w.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {w.nome}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {w.codigo || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          w.ativo
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {w.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleEdit(w)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        ✏️ Editar
                      </button>
                      {w.ativo && (
                        <button
                          onClick={() => handleDelete(w.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          🗑️ Desativar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredWarehouses.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Nenhum armazém encontrado.
            </div>
          )}
        </div>

        {/* Seção de Movimentos de Estoque */}
        {selectedWarehouseId && (
          <>
            {/* Estatísticas de Estoque do Armazém */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="flex items-center">
                  <Package className="h-8 w-8 text-blue-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Qtd. Total</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stockStats.totalQuantity.toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="flex items-center">
                  <TrendingUp className="h-8 w-8 text-green-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Disponível</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stockStats.totalAvailable.toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="flex items-center">
                  <TrendingDown className="h-8 w-8 text-red-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Reservado</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stockStats.totalReserved.toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <div className="flex items-center">
                  <TrendingDown className="h-8 w-8 text-yellow-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500">Estoque Baixo</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stockStats.lowStockItems}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filtros de Movimentos */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filtrar por tipo de movimento
                  </label>
                  <select
                    value={movementTypeFilter}
                    onChange={(e) => {
                      setMovementTypeFilter(e.target.value);
                      setMovementsPage(1);
                    }}
                    className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Todos os tipos</option>
                    <option value="IN">Entrada</option>
                    <option value="OUT">Saída</option>
                    <option value="TRANSFER">Transferência</option>
                    <option value="AJUSTE">Ajuste</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Tabela de Movimentos */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">
                  📜 Movimentos de Estoque
                  {loadingMovements && <span className="ml-2 text-sm text-gray-500">(Carregando...)</span>}
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Produto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Variante
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Qtd
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Observações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {movements.length === 0 && !loadingMovements ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                          Nenhum movimento encontrado.
                        </td>
                      </tr>
                    ) : (
                      movements.map((movement) => (
                        <tr key={movement.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {getMovementIcon(movement.tipo)}
                              <span className="text-sm font-medium text-gray-900">
                                {getMovementTypeLabel(movement.tipo)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(movement.data_mov)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {movement.produto_nome || '-'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {movement.produto_sku || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {movement.sku_variacao || '-'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {movement.cor && `Cor: ${movement.cor}`}
                              {movement.tamanho_label && ` | ${movement.tamanho_label}`}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`text-sm font-medium ${
                              movement.tipo === 'IN' ? 'text-green-600' :
                              movement.tipo === 'OUT' ? 'text-red-600' :
                              'text-gray-900'
                            }`}>
                              {movement.tipo === 'IN' ? '+' : movement.tipo === 'OUT' ? '-' : ''}
                              {Math.abs(movement.quantidade).toLocaleString('pt-BR')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {movement.observacoes || '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Paginação de Movimentos */}
              {movementsPagination.pages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setMovementsPage(p => Math.max(1, p - 1))}
                      disabled={movementsPage <= 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => setMovementsPage(p => p + 1)}
                      disabled={movementsPage >= movementsPagination.pages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Próximo
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Página {movementsPagination.page} de {movementsPagination.pages}
                        {' '}-
                        Total: {movementsPagination.total} movimentos
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <ConfirmModal
          show={isConfirmOpen}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          title="Desativar Armazém"
          message="Tem certeza que deseja desativar este armazém? Esta ação não pode ser desfeita."
          confirmText="Desativar"
          cancelText="Cancelar"
          type="danger"
        />
      </div>
    </div>
  );
};

export default WarehousesManagement;
